import { Box, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { getMenuIcon } from '../../../../config/navigation/menuIcons';
import useAppShellSx from '../../hooks/useAppShellSx';
import useSidebarTransition from '../../hooks/useSidebarTransition';
import SidebarTooltip from './SidebarTooltip';

export default function SidebarNavItem({
  item,
  collapsed,
  depth = 0,
  active,
  onNavigate,
}) {
  const theme = useTheme();
  const { sidebar, sidebarType } = useAppShellSx();
  const { getLabelSx, getNavItemLayoutSx, getRailItemWrapSx, getNavButtonSx, getIconWrapSx, getNavIconSx } =
    useSidebarTransition();
  const navLayout = getNavItemLayoutSx(collapsed, depth);
  const IconComponent = getMenuIcon(item.iconKey);
  const label = item.label || item.title;
  const disabled = !item.route || item.key === 'loading' || item.key === 'empty';

  const content = (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!disabled && item.route) {
          onNavigate(item.route);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        ...getNavButtonSx({ active, disabled, collapsed }),
        ...navLayout,
        py: collapsed ? 0 : 0.625,
        my: collapsed ? 0 : 0.125,
        transition: `${navLayout.transition}, ${theme.transitions.create(['background-color', 'color', 'box-shadow'], {
          duration: theme.transitions.duration.shorter,
        })}`,
      }}
    >
      {IconComponent && (
        <Box sx={getIconWrapSx()}>
          <IconComponent sx={getNavIconSx()} />
        </Box>
      )}
      <Box
        sx={{
          ...getLabelSx(collapsed),
          ...(!collapsed && { display: 'flex', alignItems: 'center', gap: 1.5 }),
        }}
      >
        <Typography component="span" sx={{ flex: 1, textAlign: 'left', ...sidebarType.navLabel(active) }}>
          {label}
        </Typography>
        {item.badge != null && (
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.625,
              borderRadius: 999,
              bgcolor: sidebar.accent,
              color: sidebar.badgeForeground,
              ...sidebarType.badge,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {item.badge}
          </Box>
        )}
      </Box>
      {collapsed && item.badge != null && (
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: sidebar.accent,
          }}
        />
      )}
    </Box>
  );

  if (collapsed) {
    return (
      <SidebarTooltip title={label}>
        <Box sx={getRailItemWrapSx()}>{content}</Box>
      </SidebarTooltip>
    );
  }

  return content;
}

SidebarNavItem.propTypes = {
  item: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  depth: PropTypes.number,
  active: PropTypes.bool,
  onNavigate: PropTypes.func.isRequired,
};
